/**
 * KingOfTheCourtCard — weekly mini-tournament. Top 3 in Line Sprints + Vertical.
 * Updates live, resets implicitly Monday 00:00 UTC.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Crown, Timer, Rocket, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PODIUM = {
  1: { color: "#FFD700", icon: Crown },
  2: { color: "#C0C0C0", icon: Crown },
  3: { color: "#CD7F32", icon: Crown },
};

const formatTime = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = (s - m * 60).toFixed(1);
  return m > 0 ? `${m}:${sec.padStart(4, "0")}` : `${sec}s`;
};

const KingOfTheCourtCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/king-of-court/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("king-of-court fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-5 flex items-center justify-center rounded">
        <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
      </div>
    );
  }

  const hasAny = (data?.line_sprints_top3?.length || 0) + (data?.vertical_top3?.length || 0) > 0;
  if (!data || !hasAny) {
    return (
      <div className="bg-gradient-to-r from-amber-500/10 to-zinc-900/10 border border-amber-500/30 p-5 rounded text-center" data-testid="king-empty">
        <Crown className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <p className="text-sm font-bold text-white">King of the Court — Open Throne</p>
        <p className="text-xs text-zinc-400 mt-1">No one has logged times yet this week. Be the first to take the crown 👑</p>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-amber-500/10 via-[#0A0A0A] to-orange-500/5 border border-amber-500/30 p-5 rounded space-y-4"
      data-testid="king-of-court-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-4 h-4 text-amber-400" />
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400">
              King of the Court · This Week
            </p>
          </div>
          <h3 className="text-2xl font-black text-white uppercase">Battle for the Crown</h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Resets in</p>
          <p className="text-base font-black text-amber-400 font-mono" data-testid="king-resets-in">
            {data.ends_in_hours}h
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Line Sprints podium */}
        <div className="bg-black/30 border border-white/[0.06] p-3 rounded" data-testid="king-line-sprints">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="w-3.5 h-3.5 text-orange-400" />
            <p className="text-[10px] font-mono uppercase tracking-widest text-orange-400">
              Line Sprints Champion
            </p>
          </div>
          {data.line_sprints_top3.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No logs yet — claim it</p>
          ) : (
            <div className="space-y-1">
              {data.line_sprints_top3.map((r) => {
                const p = PODIUM[r.rank];
                return (
                  <div key={r.user_id} className="flex items-center justify-between" data-testid={`king-sprint-rank-${r.rank}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Crown className="w-3 h-3 flex-shrink-0" style={{ color: p.color }} />
                      <span className="text-xs text-white truncate">{r.name}</span>
                    </div>
                    <span className="text-xs font-mono font-black text-white">{formatTime(r.best_seconds)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Vertical podium */}
        <div className="bg-black/30 border border-white/[0.06] p-3 rounded" data-testid="king-vertical">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-3.5 h-3.5 text-emerald-400" />
            <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
              Vertical Champion
            </p>
          </div>
          {data.vertical_top3.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No logs yet — claim it</p>
          ) : (
            <div className="space-y-1">
              {data.vertical_top3.map((r) => {
                const p = PODIUM[r.rank];
                return (
                  <div key={r.user_id} className="flex items-center justify-between" data-testid={`king-vert-rank-${r.rank}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Crown className="w-3 h-3 flex-shrink-0" style={{ color: p.color }} />
                      <span className="text-xs text-white truncate">{r.name}</span>
                    </div>
                    <span className="text-xs font-mono font-black text-white">{r.best_inches}&quot;</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KingOfTheCourtCard;
